// Helpers for showing an ongoing call (the Phone view and the app bar).
import { formatUsPhone, findContactByNumber, contactDisplayName } from "./contacts";

// Session states used by the SIP stack.
export const CALLING = 1;
export const RINGING = 2;
export const CONNECTED = 3;
export const INCOMING = 4;
export const ON_HOLD = 5;

// Numbers that reach the voicemail system.
const VOICEMAIL_NUMBERS = ["800", "801"];
// Names the SIP stack uses when it has no real display name yet.
const PLACEHOLDER_NAMES = ["callee", "caller", "unknown"];

export const isVoicemail = (number) => VOICEMAIL_NUMBERS.includes(String(number));

export const statusText = (status) =>
  status <= CALLING ? "Calling"
    : status === RINGING ? "Ringing"
      : status === CONNECTED ? "Active call"
        : status === INCOMING ? "Incoming call"
          : status === ON_HOLD ? "On hold" : "";

// 0:07, 12:34, 1:02:03
export const formatDuration = (ms) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const hrs = Math.floor(total / 3600);
  const mins = Math.floor((total % 3600) / 60);
  const secs = String(total % 60).padStart(2, "0");
  return hrs > 0 ? `${hrs}:${String(mins).padStart(2, "0")}:${secs}` : `${mins}:${secs}`;
};

// Name to show for a session: the contact, "Voicemail", a real SIP display
// name, or the number itself when nothing better is known.
export const sessionName = (session) => {
  if (session.contact) return session.contactName;
  if (isVoicemail(session.remoteNumber)) return "Voicemail";
  const name = String(session.contactName || "").trim();
  if (!name || PLACEHOLDER_NAMES.includes(name.toLowerCase())) return String(formatUsPhone(session.remoteNumber));
  return name;
};

// Gives each call the name of the contact its number belongs to. The SIP
// stack only knows colleagues by their extension; this also finds personal
// and external contacts, and a colleague called on another of their numbers.
// A contact's name wins over the display name the other side sent.
export const resolveSessionNames = (sessions, phonebooks) =>
  (sessions || []).map((session) => {
    if (session.contact || isVoicemail(session.remoteNumber)) return session;
    const name = contactDisplayName(findContactByNumber(phonebooks, session.remoteNumber));
    return name !== "" ? { ...session, contactName: name, lookupName: name } : session;
  });

// Number line, omitted when it would just repeat the name.
export const sessionNumber = (session) => {
  const number = String(formatUsPhone(session.remoteNumber));
  return number !== sessionName(session) ? number : null;
};

// Time since the call was answered, or null before that.
export const callTime = (session) =>
  session && session.connectedAt ? formatDuration(Date.now() - session.connectedAt) : null;

// The call to put first when there are several: a connected one, then one
// that is ringing in, then whatever is there.
export const primarySession = (sessions) =>
  sessions.find((s) => s.sessionStatus === CONNECTED)
  || sessions.find((s) => s.sessionStatus === INCOMING)
  || sessions[0]
  || null;
